import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, ArrowLeft, CheckCircle, XCircle } from "lucide-react";
import api from "@/lib/api";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await api.get(`/auth/verify-reset-token/${token}`);
      setValid(res.data.valid);
      setEmail(res.data.email);
    } catch (err) {
      setValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setResetting(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!token || !valid) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-heading font-bold text-white mb-2">Invalid Reset Link</h2>
          <p className="text-gray-400 text-sm mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-xl font-heading font-bold text-white mb-2">Password Reset Complete!</h2>
          <p className="text-gray-400 text-sm mb-6">
            Your password has been successfully reset. You can now sign in with your new password.
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold"
          >
            Sign In Now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-cyan-400" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white mb-2">Create New Password</h2>
          <p className="text-gray-400 text-sm">
            Enter a new password for <span className="text-cyan-400">{email}</span>
          </p>
        </div>

        <form onSubmit={handleResetPassword} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-gray-300 text-sm">
              New Password
            </Label>
            <Input
              id="new-password"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 
                       focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all rounded-lg"
              data-testid="new-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-gray-300 text-sm">
              Confirm Password
            </Label>
            <Input
              id="confirm-password"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 
                       focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all rounded-lg"
              data-testid="confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            disabled={resetting}
            className="w-full h-12 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold"
            data-testid="reset-submit-btn"
          >
            {resetting ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Resetting...
              </span>
            ) : (
              "Reset Password"
            )}
          </Button>
        </form>

        <p className="text-gray-500 text-xs text-center mt-6">
          Remember your password?{" "}
          <button onClick={() => navigate("/login")} className="text-cyan-400 hover:text-cyan-300">
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
